<template>
  <div class="space-y-3">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <label :for="switchId" class="text-sm font-medium">
          {{ $t("clipRange.toggle") }}
        </label>
        <p class="mt-0.5 text-xs text-muted-foreground">
          {{ $t("clipRange.toggleHint") }}
        </p>
      </div>
      <Switch
        :id="switchId"
        :model-value="modelValue.enabled"
        :disabled="disabled"
        class="mt-0.5"
        @update:model-value="setEnabled"
      />
    </div>

    <div v-if="modelValue.enabled" class="space-y-3 rounded-xl border bg-muted p-3 sm:p-4">
      <!-- Player: watch / listen to find the exact start and end -->
      <div v-if="source.kind === 'youtube' && !youtubeError" class="overflow-hidden rounded-lg border bg-background">
        <div ref="youtubeContainer" class="aspect-video w-full" />
      </div>
      <!-- A host-owned player (externalMedia) replaces the built-in one entirely. -->
      <div v-else-if="source.kind === 'file' && fileUrl && !fileError && !externalMedia" class="overflow-hidden rounded-lg border bg-background">
        <video
          v-if="!isAudioFile"
          ref="mediaElement"
          :src="fileUrl"
          controls
          playsinline
          preload="metadata"
          class="aspect-video w-full bg-background"
          @loadedmetadata="onMediaMetadata"
          @error="fileError = true"
        />
        <audio
          v-else
          ref="mediaElement"
          :src="fileUrl"
          controls
          preload="metadata"
          class="w-full"
          @loadedmetadata="onMediaMetadata"
          @error="fileError = true"
        />
      </div>
      <p v-else-if="!externalMedia" class="flex items-start gap-2 text-xs text-muted-foreground">
        <Icon name="lucide:info" class="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {{ playerFallbackText }}
          <a
            v-if="source.kind === 'youtube'"
            :href="youtubeWatchUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-primary underline-offset-4 hover:underline"
          >{{ $t("clipRange.openOnYoutube") }}</a>
        </span>
      </p>

      <!-- Range slider over the whole media -->
      <!-- A new pointer/key interaction always starts from a fresh drag origin,
           so a cancelled drag can't leak its origin into the next one. -->
      <div
        v-if="effectiveDuration > 0"
        class="space-y-2"
        @pointerdown.capture="resetDragOrigin"
        @keydown.capture="resetDragOrigin"
        @pointercancel="resetDragOrigin"
        @focusout="resetDragOrigin"
      >
        <Slider
          :model-value="[modelValue.startSeconds, modelValue.endSeconds]"
          :min="0"
          :max="sliderMax"
          :step="1"
          :min-steps-between-thumbs="MIN_CLIP_SECONDS"
          :dir="dir"
          :disabled="disabled"
          :thumb-labels="[$t('clipRange.start'), $t('clipRange.end')]"
          :thumb-value-texts="[formatTimecode(modelValue.startSeconds, forceHours), formatTimecode(modelValue.endSeconds, forceHours)]"
          @update:model-value="onSliderInput"
          @value-commit="onSliderCommit"
        />
        <div class="flex items-start justify-between gap-3 text-xs">
          <span class="font-mono text-subtle-foreground">{{ formatTimecode(0, forceHours) }}</span>
          <!-- Clip length sits between the two ends of the bar it describes. -->
          <span
            v-if="!validationError"
            :id="`${switchId}-hint`"
            class="min-w-0 text-center text-muted-foreground"
          >
            {{ $t("clipRange.summary", { length: formatLength(clipLength), total: formatLength(effectiveDuration) }) }}<template v-if="estimateText"> · {{ estimateText }}</template>
          </span>
          <span class="font-mono text-subtle-foreground">{{ formatTimecode(effectiveDuration, forceHours) }}</span>
        </div>
      </div>

      <!-- Start / end fields and the timestamp mode, side by side -->
      <div class="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div v-for="edge in edges" :key="edge.key" class="space-y-1.5">
          <label :for="`${switchId}-${edge.key}`" class="block text-xs font-medium text-muted-foreground">
            {{ edge.label }}
          </label>
          <div class="flex items-center gap-1.5">
            <Input
              :id="`${switchId}-${edge.key}`"
              v-model="drafts[edge.key]"
              inputmode="numeric"
              autocomplete="off"
              dir="ltr"
              :placeholder="forceHours ? '00:00:00' : '00:00'"
              :disabled="disabled"
              :aria-invalid="Boolean(validationError)"
              :aria-describedby="`${switchId}-hint`"
              :class="['bg-background text-center font-mono', forceHours ? 'w-28' : 'w-24']"
              @blur="commitDraft(edge.key)"
              @keydown.enter.prevent="commitDraft(edge.key)"
            />
            <Button
              v-if="hasPlayer"
              type="button"
              size="icon"
              variant="soft"
              class="shrink-0"
              :disabled="disabled"
              :aria-label="edge.setLabel"
              :title="edge.setLabel"
              @click="setFromPlayer(edge.key)"
            >
              <Icon name="lucide:map-pin" class="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              v-if="hasPlayer"
              type="button"
              size="icon"
              variant="outline"
              class="shrink-0 bg-background"
              :disabled="disabled"
              :aria-label="$t('clipRange.playFrom', { time: formatTimecode(edge.value, forceHours) })"
              :title="$t('clipRange.playFrom', { time: formatTimecode(edge.value, forceHours) })"
              @click="seekTo(edge.value)"
            >
              <Icon name="lucide:play" class="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <!-- Subtitle timestamps: original timeline or restart at 00:00 -->
        <div v-if="showTimestampMode" class="space-y-1.5">
          <p :id="`${switchId}-mode`" class="text-xs font-medium text-muted-foreground">
            {{ $t("clipRange.timestampMode") }}
          </p>
          <div
            role="radiogroup"
            :aria-labelledby="`${switchId}-mode`"
            class="inline-flex min-h-10 flex-wrap items-center gap-1 rounded-full border bg-background p-1"
          >
            <button
              v-for="(mode, index) in timestampModes"
              :key="mode.value"
              type="button"
              role="radio"
              :aria-checked="modelValue.timestampMode === mode.value"
              :tabindex="index === checkedModeIndex ? 0 : -1"
              :disabled="disabled"
              :class="[
                'rounded-full px-3 py-1.5 text-xs font-medium transition-colors [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                modelValue.timestampMode === mode.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              ]"
              @click="update({ timestampMode: mode.value })"
              @keydown="onModeKeydown($event, index)"
            >
              {{ mode.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Error, or the format hint while the length is unknown (the summary lives under the bar otherwise) -->
      <p v-if="validationError" :id="`${switchId}-hint`" class="text-xs font-medium text-destructive" role="alert">
        {{ validationError }}
      </p>
      <p v-else-if="effectiveDuration <= 0" :id="`${switchId}-hint`" class="text-xs text-muted-foreground">
        {{ $t("clipRange.formatHint") }}<template v-if="estimateText"> · {{ estimateText }}</template>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  MIN_CLIP_SECONDS,
  formatTimecode,
  normalizeClipRange,
  parseTimecode,
  type ClipRangeSource,
  type ClipRangeState,
} from "~/utils/clip-range";
import { useYoutubePlayer } from "~/composables/useYoutubePlayer";

const props = withDefaults(
  defineProps<{
    modelValue: ClipRangeState;
    source: ClipRangeSource;
    /** Known media length in seconds; 0 when unknown (the player may fill it in). */
    durationSeconds?: number;
    showTimestampMode?: boolean;
    /** Appended to the summary line, e.g. "≈ 170 credits". */
    estimateText?: string;
    disabled?: boolean;
    /**
     * A player the host page already renders for the same file (e.g. the
     * hardsub region picker). When set, no built-in player is created and the
     * current time / seeking / duration come from this element instead.
     */
    externalMedia?: HTMLMediaElement | null;
  }>(),
  { durationSeconds: 0, showTimestampMode: false, estimateText: "", disabled: false, externalMedia: null }
);

const emit = defineEmits<{
  "update:modelValue": [value: ClipRangeState];
  /** Media length learned from the player, for callers without one. */
  duration: [seconds: number];
}>();

const { t } = useI18n();
const switchId = useId();
// The slider needs the reading direction explicitly.
const isRtl = useIsRtl();
const dir = computed(() => (isRtl.value ? "rtl" : "ltr"));

const update = (patch: Partial<ClipRangeState>) =>
  emit("update:modelValue", { ...props.modelValue, ...patch });

// --- Player ---------------------------------------------------------------

const youtubeContainer = ref<HTMLElement | null>(null);
const youtubeVideoId = computed(() =>
  props.source.kind === "youtube" ? props.source.videoId : ""
);
const youtube = useYoutubePlayer(youtubeContainer, youtubeVideoId);
const youtubeError = computed(() => Boolean(youtube.error.value));
const youtubeWatchUrl = computed(() => {
  const start = Math.floor(props.modelValue.startSeconds || 0);
  return `https://www.youtube.com/watch?v=${youtubeVideoId.value}${start ? `&t=${start}s` : ""}`;
});

const mediaElement = ref<HTMLMediaElement | null>(null);
const fileUrl = ref("");
const fileError = ref(false);
const fileDuration = ref(0);
const isAudioFile = computed(
  () => props.source.kind === "file" && props.source.file.type.startsWith("audio/")
);

watch(
  () => (props.source.kind === "file" && !props.externalMedia ? props.source.file : null),
  (file) => {
    if (fileUrl.value) URL.revokeObjectURL(fileUrl.value);
    fileUrl.value = file && import.meta.client ? URL.createObjectURL(file) : "";
    fileError.value = false;
    fileDuration.value = 0;
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  if (fileUrl.value) URL.revokeObjectURL(fileUrl.value);
  detachExternalMedia();
});

const readDuration = (media: HTMLMediaElement | null | undefined) => {
  const seconds = Number(media?.duration);
  if (Number.isFinite(seconds) && seconds > 0) fileDuration.value = seconds;
};

const onMediaMetadata = () => readDuration(mediaElement.value);

// External player: its length may already be known when it is handed over,
// otherwise it arrives with its own loadedmetadata.
let externalMediaListener: { media: HTMLMediaElement; handler: () => void } | null = null;
const detachExternalMedia = () => {
  if (!externalMediaListener) return;
  externalMediaListener.media.removeEventListener("loadedmetadata", externalMediaListener.handler);
  externalMediaListener = null;
};
watch(
  () => props.externalMedia,
  (media) => {
    detachExternalMedia();
    if (!media) return;
    fileDuration.value = 0;
    readDuration(media);
    const handler = () => readDuration(media);
    media.addEventListener("loadedmetadata", handler);
    externalMediaListener = { media, handler };
  },
  { immediate: true }
);

const hasPlayer = computed(() =>
  props.source.kind === "youtube"
    ? youtube.ready.value && !youtubeError.value
    : props.source.kind === "file"
      ? Boolean(props.externalMedia) || (Boolean(fileUrl.value) && !fileError.value)
      : false
);

const playerFallbackText = computed(() => {
  if (props.source.kind === "youtube") return t("clipRange.playerUnavailable");
  if (props.source.kind === "file") return t("clipRange.fileNotPlayable");
  return t("clipRange.noPlayer");
});

const fileMedia = () => props.externalMedia || mediaElement.value;

const currentTime = () =>
  props.source.kind === "youtube"
    ? youtube.getCurrentTime()
    : Number(fileMedia()?.currentTime) || 0;

const seekTo = (seconds: number) => {
  if (props.source.kind === "youtube") {
    youtube.seekTo(seconds);
    return;
  }
  const media = fileMedia();
  if (media) {
    media.currentTime = Math.max(0, seconds);
    media.play().catch(() => {});
  }
};

// --- Range ----------------------------------------------------------------

const effectiveDuration = computed(() =>
  Math.floor(props.durationSeconds || youtube.duration.value || fileDuration.value || 0)
);
const sliderMax = computed(() => Math.max(effectiveDuration.value, MIN_CLIP_SECONDS));
const forceHours = computed(
  () => Math.max(effectiveDuration.value, props.modelValue.endSeconds) >= 3600
);

watch(
  () => youtube.duration.value || fileDuration.value,
  (seconds) => {
    if (seconds > 0) emit("duration", Math.floor(seconds));
  }
);

// Open with the whole media selected, and fill the end in once the length
// becomes known.
watch(
  [() => props.modelValue.enabled, effectiveDuration],
  ([enabled, duration]) => {
    if (enabled && duration > 0 && props.modelValue.endSeconds <= 0) {
      update({ startSeconds: 0, endSeconds: duration });
    }
  },
  { immediate: true }
);

const setEnabled = (enabled: boolean) => update({ enabled });

const clipLength = computed(() =>
  Math.max(0, props.modelValue.endSeconds - props.modelValue.startSeconds)
);

const validationError = computed(() => {
  // No end yet (length still loading): show the format hint, not an error.
  if (!props.modelValue.enabled || props.modelValue.endSeconds <= 0) return "";
  const result = normalizeClipRange(
    { startSeconds: props.modelValue.startSeconds, endSeconds: props.modelValue.endSeconds },
    effectiveDuration.value,
    { allowUnknownDuration: true }
  );
  return result.ok ? "" : t("clipRange.errorInvalid", { seconds: MIN_CLIP_SECONDS });
});

const formatLength = (seconds: number) => formatTimecode(seconds, seconds >= 3600);

type EdgeKey = "start" | "end";
const drafts = reactive<Record<EdgeKey, string>>({ start: "", end: "" });

watch(
  [() => props.modelValue.startSeconds, () => props.modelValue.endSeconds, forceHours],
  ([start, end]) => {
    drafts.start = formatTimecode(start, forceHours.value);
    drafts.end = end > 0 ? formatTimecode(end, forceHours.value) : "";
  },
  { immediate: true }
);

const clampToDuration = (seconds: number) =>
  effectiveDuration.value > 0 ? Math.min(seconds, effectiveDuration.value) : seconds;

const commitDraft = (key: EdgeKey) => {
  const parsed = parseTimecode(drafts[key]);
  if (parsed === null) {
    // Restore the last valid value instead of keeping garbage in the field.
    const current = key === "start" ? props.modelValue.startSeconds : props.modelValue.endSeconds;
    drafts[key] = current > 0 || key === "start" ? formatTimecode(current, forceHours.value) : "";
    return;
  }
  const seconds = Math.floor(clampToDuration(parsed));
  update(key === "start" ? { startSeconds: seconds } : { endSeconds: seconds });
};

const setFromPlayer = (key: EdgeKey) => {
  const seconds = Math.floor(clampToDuration(currentTime()));
  update(key === "start" ? { startSeconds: seconds } : { endSeconds: seconds });
};

const edges = computed(() => [
  {
    key: "start" as const,
    label: t("clipRange.start"),
    setLabel: t("clipRange.setStart"),
    value: props.modelValue.startSeconds,
  },
  {
    key: "end" as const,
    label: t("clipRange.end"),
    setLabel: t("clipRange.setEnd"),
    value: props.modelValue.endSeconds,
  },
]);

// Dragging updates the numbers live; releasing a thumb seeks the player to
// it so the user can hear exactly where the cut lands.
let dragOrigin: [number, number] | null = null;
const resetDragOrigin = () => {
  dragOrigin = null;
};
const onSliderInput = (value: number[] | undefined) => {
  if (!value || value.length < 2) return;
  dragOrigin ??= [props.modelValue.startSeconds, props.modelValue.endSeconds];
  update({ startSeconds: value[0], endSeconds: value[1] });
};
const onSliderCommit = (value: number[]) => {
  if (!value || value.length < 2) return;
  const origin = dragOrigin ?? [props.modelValue.startSeconds, props.modelValue.endSeconds];
  dragOrigin = null;
  const moved = value[0] !== origin[0] ? value[0] : value[1];
  if (hasPlayer.value) seekTo(moved);
};

const timestampModes = computed(() => [
  { value: "original" as const, label: t("clipRange.timestampOriginal") },
  { value: "zero" as const, label: t("clipRange.timestampZero") },
]);

// Radio group keyboard pattern: roving tabindex, arrows move + select
// (Left/Right follow reading direction), Home/End jump to the ends.
const checkedModeIndex = computed(() =>
  Math.max(0, timestampModes.value.findIndex((mode) => mode.value === props.modelValue.timestampMode))
);
const onModeKeydown = (event: KeyboardEvent, index: number) => {
  const count = timestampModes.value.length;
  const forward = isRtl.value ? "ArrowLeft" : "ArrowRight";
  const backward = isRtl.value ? "ArrowRight" : "ArrowLeft";
  let next: number;
  if (event.key === forward || event.key === "ArrowDown") next = (index + 1) % count;
  else if (event.key === backward || event.key === "ArrowUp") next = (index - 1 + count) % count;
  else if (event.key === "Home") next = 0;
  else if (event.key === "End") next = count - 1;
  else return;
  const target = timestampModes.value[next];
  if (!target) return;
  event.preventDefault();
  update({ timestampMode: target.value });
  // Sibling lookup (refs in v-for are not guaranteed to keep render order)
  const radios = (event.currentTarget as HTMLElement | null)?.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
  radios?.[next]?.focus();
};
</script>
